import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useProfile } from "@hooks";
import { formattedDate } from "@utils/helpers";
import { selectChatMessages, selectChatJoinedAt } from "@store/chatSlice";
import ChatMessageRow from "./ChatMessageRow";
import ChatEmptyState from "./ChatEmptyState";
import ChatJumpToLatest from "./ChatJumpToLatest";

const GROUP_WINDOW_MS = 5 * 60 * 1000;
const AT_BOTTOM_THRESHOLD_PX = 40;

const JoinDivider = ({ joinedAt }) => {
  return (
    <Flex align="center" gap="8px" my="4px">
      <Box flex="1" h="1px" bg="dark.400" />
      <Text fontSize="10px" color="dark.200" whiteSpace="nowrap">
        You joined · {formattedDate(joinedAt, "h:mm A")}
      </Text>
      <Box flex="1" h="1px" bg="dark.400" />
    </Flex>
  );
};

const ChatMessageList = ({ isActive, onRetry, onDiscard }) => {
  const messages = useSelector(selectChatMessages);
  const joinedAt = useSelector(selectChatJoinedAt);
  const { profile } = useProfile();

  const containerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const previousLengthRef = useRef(0);
  const [newMessagesCount, setNewMessagesCount] = useState(0);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, []);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const atBottom = distanceFromBottom <= AT_BOTTOM_THRESHOLD_PX;
    isAtBottomRef.current = atBottom;
    if (atBottom) {
      setNewMessagesCount(0);
    }
  }, []);

  useEffect(() => {
    const addedCount = messages.length - previousLengthRef.current;
    previousLengthRef.current = messages.length;
    if (addedCount <= 0) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.isOwn || isAtBottomRef.current) {
      scrollToBottom();
      setNewMessagesCount(0);
    } else {
      setNewMessagesCount((count) => count + addedCount);
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!isActive) return undefined;
    const rafId = requestAnimationFrame(() => {
      isAtBottomRef.current = true;
      scrollToBottom();
      setNewMessagesCount(0);
    });
    return () => cancelAnimationFrame(rafId);
  }, [isActive, scrollToBottom]);

  const handleJumpToLatest = () => {
    isAtBottomRef.current = true;
    scrollToBottom();
    setNewMessagesCount(0);
  };

  if (messages.length === 0) {
    return <ChatEmptyState />;
  }

  const hasBacklogRows =
    Boolean(joinedAt) && messages.some((message) => message.sentAt < joinedAt);
  const hasPostJoinRows =
    Boolean(joinedAt) && messages.some((message) => message.sentAt >= joinedAt);
  let hasRenderedDivider = false;

  return (
    <Flex direction="column" flex="1" minH="0" position="relative">
      <Text
        fontSize="10px"
        color="dark.300"
        textAlign="center"
        pt="2px"
        pb="8px"
      >
        Chat is cleared when the meeting ends
      </Text>
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        flex="1"
        overflowY="auto"
        display="flex"
        flexDirection="column"
        sx={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "#4A5568",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": { background: "#2D3748" },
        }}
      >
        <Flex direction="column" gap="10px" mt="auto">
          {messages.map((message, index) => {
            const previousMessage = index > 0 ? messages[index - 1] : null;
            const isFirstAfterJoin =
              Boolean(joinedAt) &&
              message.sentAt >= joinedAt &&
              (!previousMessage || previousMessage.sentAt < joinedAt);
            const showDivider =
              hasBacklogRows && !hasRenderedDivider && isFirstAfterJoin;
            if (showDivider) {
              hasRenderedDivider = true;
            }

            const isGrouped =
              !showDivider &&
              Boolean(previousMessage) &&
              previousMessage.senderId === message.senderId &&
              message.sentAt - previousMessage.sentAt <= GROUP_WINDOW_MS;

            return (
              <Box key={message.clientMessageId || message.id}>
                {showDivider && <JoinDivider joinedAt={joinedAt} />}
                <ChatMessageRow
                  message={message}
                  isGrouped={isGrouped}
                  profile={profile}
                  onRetry={onRetry}
                  onDiscard={onDiscard}
                />
              </Box>
            );
          })}
          {hasBacklogRows && !hasPostJoinRows && (
            <JoinDivider joinedAt={joinedAt} />
          )}
        </Flex>
      </Box>
      {newMessagesCount > 0 && (
        <ChatJumpToLatest
          count={newMessagesCount}
          onClick={handleJumpToLatest}
        />
      )}
    </Flex>
  );
};

export default ChatMessageList;
